
import { transformKeysToCamelCase } from '@/Utils/ObjectUtility.js';

import {
    backerPaymentsSortType,
    backerPaymentsSearchType,
} from '@/Models';

import {
    enumToSelectOptions,
    mapEnumDisplayNameFromResource,
} from '@/Utils/EnumUtility';

import { fieldTypes } from '@/Types/FieldTypes';

import Resources from '@/Resources.js';
import Formatter from '@/Utils/Formatter.js';

export const filterBackerCenterPaymentsTypes = {
    areOnlyCanceledPaymentsVisible: 0,
    minPaymentDate: 1,
    maxPaymentDate: 2,
    minAmount: 3,
    maxAmount: 4,
    term: 5,
    areOnlyInstallmentPaymentsVisible: 6,
    includeArchivedProjects: 7,
};

export function getFilterGroupDisplayName(filterType) {
    const filterGroupDisplayNames = {
        [filterBackerCenterPaymentsTypes.areOnlyCanceledPaymentsVisible]: Resources.BackerCenter.PaymentStatusFilterLabel,
        [filterBackerCenterPaymentsTypes.minPaymentDate]: Resources.BackerCenter.PaymentDateFromFilterLabel,
        [filterBackerCenterPaymentsTypes.maxPaymentDate]: Resources.BackerCenter.PaymentDateToFilterLabel,
        [filterBackerCenterPaymentsTypes.minAmount]: Resources.BackerCenter.PaymentAmountFromFilterLabel,
        [filterBackerCenterPaymentsTypes.maxAmount]: Resources.BackerCenter.PaymentAmountToFilterLabel,
        [filterBackerCenterPaymentsTypes.term]: Resources.Search.SearchFiltersGroupSearchPhraseLabel,
        [filterBackerCenterPaymentsTypes.areOnlyInstallmentPaymentsVisible]: Resources.BackerCenter.InstallmentsFilterLabel,
        [filterBackerCenterPaymentsTypes.includeArchivedProjects]: Resources.BackerCenter.ArchivedProjectsFilterLabel,
    };
    return filterGroupDisplayNames[filterType];
}

export function buildDefaultPaymentsFilterParams(params = {}) {
    // Convert string enum names to numeric values

    const formattedParams = transformKeysToCamelCase(params);
    let sortType = formattedParams.sortType || formattedParams.sortType || 0;
    if (typeof sortType === 'string') {
        sortType = backerPaymentsSortType[sortType.toLowerCase()] ?? 0;
    }
    let searchType = formattedParams.SearchType || formattedParams.searchType;
    if (typeof searchType === 'string') {
        searchType = backerPaymentsSearchType[searchType.toLowerCase()] ?? null;
    }
    return {
        areOnlyCanceledPaymentsVisible: !!formattedParams.areOnlyCanceledPaymentsVisible || null,
        areOnlyInstallmentPaymentsVisible: !!formattedParams.areOnlyInstallmentPaymentsVisible || null,
        includeArchivedProjects: formattedParams.includeArchivedProjects ?? null,
        maxPaymentDate: formattedParams.maxPaymentDate ? new Date(formattedParams.maxPaymentDate) : null,
        minPaymentDate: formattedParams.minPaymentDate ? new Date(formattedParams.minPaymentDate) : null,
        maxAmount: formattedParams.maxAmount ?? null,
        minAmount: formattedParams.minAmount ?? null,
        term: formattedParams.term || '',
        sortType,
        searchType,
    };
}

export function buildPaymentsCurrentFilter(searchParams) {
    const config = [
        {
            key: 'minPaymentDate',
            type: filterBackerCenterPaymentsTypes.minPaymentDate,
            isEnabled: () => searchParams.minPaymentDate !== null,
            label: () => Formatter.format(searchParams.minPaymentDate, 'd'),
        },
        {
            key: 'maxPaymentDate',
            type: filterBackerCenterPaymentsTypes.maxPaymentDate,
            isEnabled: () => searchParams.maxPaymentDate !== null,
            label: () => Formatter.format(searchParams.maxPaymentDate, 'd'),
        },
        {
            key: 'minAmount',
            type: filterBackerCenterPaymentsTypes.minAmount,
            isEnabled: () => searchParams.minAmount !== null,
            label: () => searchParams.minAmount,
        },
        {
            key: 'maxAmount',
            type: filterBackerCenterPaymentsTypes.maxAmount,
            isEnabled: () => searchParams.maxAmount !== null,
            label: () => searchParams.maxAmount,
        },
        {
            key: 'term',
            type: filterBackerCenterPaymentsTypes.term,
            isEnabled: () => !!searchParams.term,
            label: () => searchParams.term,
        },
        {
            key: 'areOnlyInstallmentPaymentsVisible',
            type: filterBackerCenterPaymentsTypes.areOnlyInstallmentPaymentsVisible,
            isEnabled: () => searchParams.areOnlyInstallmentPaymentsVisible === true,
            label: () => Resources.BackerCenter.ShowPledgesWithInstallmentsOnlyLabel,
            groupLabelHidden: true,
        },
        {
            key: 'areOnlyCanceledPaymentsVisible',
            type: filterBackerCenterPaymentsTypes.areOnlyCanceledPaymentsVisible,
            isEnabled: () => searchParams.areOnlyCanceledPaymentsVisible === true,
            label: () => Resources.BackerCenter.FailedPaymentsOnlyLabel,
            groupLabelHidden: true,
        },
        {
            key: 'includeArchivedProjects',
            type: filterBackerCenterPaymentsTypes.includeArchivedProjects,
            isEnabled: () => searchParams.includeArchivedProjects === true,
            label: () => Resources.BackerCenter.IncludeArchivedProjectsLabel,
            groupLabelHidden: true,
        },
    ];
    return config
        .filter(item => item.isEnabled())
        .map(item => ({
            key: item.key,
            group: item.groupLabelHidden ? null : getFilterGroupDisplayName(item.type),
            label: item.label(),
            filterSearchType: item.type,
        }));
}

export function removeSearchSelectedFilter(filter, searchParams) {
    const result = { ...searchParams };

    const strategies = {
        [filterBackerCenterPaymentsTypes.minPaymentDate]: () => {
            result.minPaymentDate = null;
        },
        [filterBackerCenterPaymentsTypes.maxPaymentDate]: () => {
            result.maxPaymentDate = null;
        },
        [filterBackerCenterPaymentsTypes.minAmount]: () => {
            result.minAmount = null;
        },
        [filterBackerCenterPaymentsTypes.maxAmount]: () => {
            result.maxAmount = null;
        },
        [filterBackerCenterPaymentsTypes.term]: () => {
            result.term = '';
        },
        [filterBackerCenterPaymentsTypes.areOnlyInstallmentPaymentsVisible]: () => {
            result.areOnlyInstallmentPaymentsVisible = null;
        },
        [filterBackerCenterPaymentsTypes.areOnlyCanceledPaymentsVisible]: () => {
            result.areOnlyCanceledPaymentsVisible = null;
        },
        [filterBackerCenterPaymentsTypes.includeArchivedProjects]: () => {
            result.includeArchivedProjects = null;
        },
    };

    const strategy = strategies[filter.filterSearchType];
    if (strategy) strategy();

    return result;
}

export function buildPaymentsSortList() {
    return [
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'sortType',
            label: Resources.Search.SearchProjectSortGroupLabel,
            options: enumToSelectOptions(
                mapEnumDisplayNameFromResource(Resources.BackerCenter, backerPaymentsSortType),
            ),
            props: {
                type: 'radio',
                showLabel: false,
            },
        },
    ];
}

export function buildPaymentsFilterList() {
    return [
        {
            type: fieldTypes.date,
            modelName: 'minPaymentDate',
            label: getFilterGroupDisplayName(filterBackerCenterPaymentsTypes.minPaymentDate),
            props: {
                valueType: 'date',
            },
            qa: 'PaymentDateFrom',
        },
        {
            type: fieldTypes.date,
            modelName: 'maxPaymentDate',
            label: getFilterGroupDisplayName(filterBackerCenterPaymentsTypes.maxPaymentDate),
            props: {
                valueType: 'date',
            },
            qa: 'PaymentDateTo',
        },
        {
            type: fieldTypes.number,
            modelName: 'minAmount',
            label: getFilterGroupDisplayName(filterBackerCenterPaymentsTypes.minAmount),
            rules: { max: 10 },
            props: {
                textAlign: 'left',
            },
            qa: 'PaymentAmountFrom',
        },
        {
            type: fieldTypes.number,
            modelName: 'maxAmount',
            label: getFilterGroupDisplayName(filterBackerCenterPaymentsTypes.maxAmount),
            rules: { max: 10 },
            props: {
                textAlign: 'left',
            },
            qa: 'PaymentAmountTo',
        },
        {
            type: fieldTypes.text,
            modelName: 'term',
            rules: { max: 80 },
            label: getFilterGroupDisplayName(filterBackerCenterPaymentsTypes.term),
            placeholder: Resources.Search.SearchProjectsFiltersSearchPhrasePlaceholder,
            qa: 'SearchTerm',
        },
        {
            type: fieldTypes.singleStateCheckboxField,
            modelName: 'areOnlyInstallmentPaymentsVisible',
            label: getFilterGroupDisplayName(filterBackerCenterPaymentsTypes.areOnlyInstallmentPaymentsVisible),
            props: {
                checkedValue: true,
                customLabelText: Resources.BackerCenter.ShowPledgesWithInstallmentsOnlyLabel,
                uncheckedValue: false,
            },
            qa: 'InstallmentPaymentsOnly',
        },
        {
            type: fieldTypes.singleStateCheckboxField,
            modelName: 'areOnlyCanceledPaymentsVisible',
            label: getFilterGroupDisplayName(filterBackerCenterPaymentsTypes.areOnlyCanceledPaymentsVisible),
            props: {
                checkedValue: true,
                customLabelText: Resources.BackerCenter.FailedPaymentsOnlyLabel,
                uncheckedValue: false,
            },
            qa: 'CanceledPaymentsOnly',
        },
        {
            type: fieldTypes.singleStateCheckboxField,
            modelName: 'includeArchivedProjects',
            label: getFilterGroupDisplayName(filterBackerCenterPaymentsTypes.includeArchivedProjects),
            props: {
                customLabelText: Resources.BackerCenter.IncludeArchivedProjectsLabel,
            },
            qa: 'includeArchivedProjects',
        },
    ];
}
